"use client"

import { useState } from "react"
import { updateDoc, doc } from "firebase/firestore"
import { useAuth } from "../context/AuthContext"
import { db } from "../firebase"
import { useNavigate } from "react-router-dom"
import { checkIfUserCanBet, createTransaction, sendNotification } from "../database/Functions"
import { FaShareFromSquare } from "react-icons/fa6"
import { getTeamLogoWithFallback } from "../utils/teamLogoUtils"

function Bet({ bet, onBetAccepted }) {
  const { currentUser, userProfile } = useAuth()
  const navigate = useNavigate()
  const [eventImage] = useState("sports_video.png")

  const acceptBet = async () => {
    try {
      if (!userProfile) {
        console.error("User data not found")
        return
      }

      let account_balance = userProfile?.account_balance
      const wagerAmount = bet.wager_amount
      const id = bet.id

      const canBet = await checkIfUserCanBet(bet.uniqueId)

      // Only proceed if the user can bet
      if (!canBet) {
        return
      }

      if (account_balance >= wagerAmount) {
        const beforeBalance = account_balance
        account_balance -= wagerAmount

        const uid = currentUser.uid

        const data = {
          accepted: "Yes",
          bet_acceptor_uid: uid,
          status: "Active",
        }

        await updateDoc(doc(db, "ActiveBets", id), data)

        await updateDoc(doc(db, "users", uid), { account_balance })

        callAcceptedBetAlert()

        createTransaction(uid, "Wager", "Accept Wager", beforeBalance, account_balance, wagerAmount, bet)

        const notificationMessage = "accepted your bet: " + bet.home_team + " vs " + bet.away_team

        await sendNotification(
          "AcceptBet",
          userProfile.full_name,
          currentUser.uid,
          bet.user_id,
          notificationMessage,
          "Bet",
          userProfile.profile_picture,
        )

        // Notify FriendsBets component
        if (onBetAccepted) {
          onBetAccepted(id)
        }

        window.location.reload()
      } else {
        alert("Insufficient Funds:  $" + userProfile?.account_balance)
      }
    } catch (error) {
      console.error("Error accepting bet:", error)
    }
  }

  const callAcceptedBetAlert = () => {
    alert("You Have Accepted This Bet. Waiting for results...")
    navigate("/")
  }

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString)
      return {
        day: date.toLocaleDateString("en-US", {
          weekday: "short",
        }),
        month: date.toLocaleDateString("en-US", {
          month: "short",
        }),
        dayNum: date.toLocaleDateString("en-US", {
          day: "2-digit",
        }),
        year: date.getFullYear(),
        time: date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
        timezone: "CDT",
      }
    } catch (error) {
      return {
        day: "Invalid",
        month: "Invalid",
        dayNum: "XX",
        year: "XXXX",
        time: "12:00PM",
        timezone: "CDT",
      }
    }
  }

  /* function getImageFileNameByTitle(title) {
    // Find the sport object that matches the title
    const sports = [
      { title: "MLB", icon: "sports_soccer.png" },
      { title: "NBA", icon: "sports_basketball.png" },
      { title: "NCAAB", icon: "sports_basketball.png" },
      { title: "Boxing", icon: "sports_mma.png" },
      { title: "NFL", icon: "sports_football-1.png" },
      { title: "NCAAF", icon: "sports_football-1.png" },
      { title: "Golf", icon: "sports_tennis.png" },
      { title: "MMA", icon: "sports_martial_arts.png" },
      { title: "Tennis", icon: "sports_tennis.png" },
    ]

    const sport = sports.find((item) => item.title === title)

    // If a match is found, extract the file name from the src path
    if (sport) {
      setEventImage(sport.icon)
      return sport.icon
    }

    // Return null if no match is found
    return "sports_video.png"
  } */

  const formattedDate = formatDate(bet.bet_time)
  const sportKey = bet.bet_sport_key || bet.sport_key
  const homeLogo = getTeamLogoWithFallback(bet.home_team, sportKey)
  const awayLogo = getTeamLogoWithFallback(bet.away_team, sportKey)

  return (
    <div className="rounded-xl overflow-hidden bg-gray-800/50 backdrop-blur-sm mb-4 flex flex-col border-0 md:border md:border-gray-700 hover:border-emerald-500/30 transition-all duration-300">
      {/* Header */}
      <div className="grid grid-cols-3 items-center p-3 md:p-4 border-b border-gray-700/50">
        <div className="flex items-center">
          <span className="text-white text-xs md:text-sm font-medium pr-1 md:pr-2">{bet.bet_sport_title}</span>
          <div className="bg-emerald-500/20 p-1 md:p-1.5 rounded-lg">
            <img
              src={require(`../assets/MenuImages/${eventImage || "/placeholder.svg"}`)}
              alt="sport icon"
              className="w-4 h-4 md:w-5 md:h-5"
            />
          </div>
        </div>
        <div className="text-white text-xs text-center">
          {formattedDate.day} {formattedDate.month} {formattedDate.dayNum} {formattedDate.year}
        </div>
        <div className="text-white text-xs md:text-sm text-right">{formattedDate.time} CDT</div>
      </div>

      <div className="border-t border-emerald-500/20 mx-4 my-2"></div>

      {/* User and Odds */}
      <div className="p-3 md:p-4 bg-gray-900/30 backdrop-blur-sm">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 md:gap-3">
            <img
              src="https://img.freepik.com/premium-photo/3d-style-avatar-profile-picture-featuring-male-character-generative-ai_739548-13626.jpg"
              alt="User avatar"
              className="w-6 h-6 md:w-8 md:h-8 rounded-full border border-gray-700"
            />
            <span className="text-white text-xs md:text-sm font-medium">{bet.user_name}</span>
          </div>
          <span className="text-white text-xs md:text-sm">{bet.user_pick}</span>
          <span className="text-emerald-400 font-semibold text-xs md:text-sm">-120</span>
        </div>
      </div>

      <div className="border-t border-gray-700/30 mx-4"></div>

      {/* Teams */}
      <div className="p-3 md:p-4 bg-gray-900/30 backdrop-blur-sm flex-grow flex flex-col justify-center">
        {/* Home Team */}
        <div className="grid grid-cols-3 items-center mb-3 md:mb-4">
          <div className="flex items-center gap-1 md:gap-2">
            <div className="w-1 h-3 md:h-4 bg-green-500 rounded-full"></div>
            <span className="text-white text-xs md:text-sm">Home</span>
          </div>
          <div className="flex items-center justify-center gap-2 min-w-0">
            <img
              src={homeLogo}
              alt={bet.home_team}
              className="w-5 h-5 rounded-full bg-gray-900/60 border border-gray-700 object-contain p-0.5 shrink-0"
              onError={(e) => {
                e.currentTarget.onerror = null
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(bet.home_team)}&background=1f2937&color=ffffff&size=96`
              }}
            />
            <span className="text-white text-xs md:text-sm text-center font-medium truncate">{bet.home_team}</span>
          </div>
          <div className="flex justify-end">
            {bet.user_pick === bet.away_team && (
              <button
                onClick={() => acceptBet(bet.home_team)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 md:px-4 py-1 md:py-1.5 rounded-full text-xs md:text-sm transition-colors"
              >
                Select
              </button>
            )}
          </div>
        </div>

        {/* Away Team */}
        <div className="grid grid-cols-3 items-center">
          <div className="flex items-center gap-1 md:gap-2">
            <div className="w-1 h-3 md:h-4 bg-blue-400 rounded-full"></div>
            <span className="text-white text-xs md:text-sm">Away</span>
          </div>
          <div className="flex items-center justify-center gap-2 min-w-0">
            <img
              src={awayLogo}
              alt={bet.away_team}
              className="w-5 h-5 rounded-full bg-gray-900/60 border border-gray-700 object-contain p-0.5 shrink-0"
              onError={(e) => {
                e.currentTarget.onerror = null
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(bet.away_team)}&background=1f2937&color=ffffff&size=96`
              }}
            />
            <span className="text-white text-xs md:text-sm text-center font-medium truncate">{bet.away_team}</span>
          </div>
          <div className="flex justify-end">
            {bet.user_pick === bet.home_team && (
              <button
                onClick={() => acceptBet(bet.away_team)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 md:px-4 py-1 md:py-1.5 rounded-full text-xs md:text-sm transition-colors"
              >
                Select
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Wager Footer */}
      <div className="bg-emerald-500 bg-opacity-90 p-2 md:p-3 flex justify-between items-center">
        <span className="text-white text-xs md:text-sm font-medium">Wager: ${bet.wager_amount}</span>
        <div className="flex items-center">
          <span className="text-white text-xs md:text-sm font-medium mr-2 md:mr-4">To Win: ${bet.payout}</span>
          <button
            className="bg-emerald-600 hover:bg-emerald-700 p-1 md:p-1.5 rounded-full transition-colors"
            onClick={() => {
              const shareUrl = `https://www.rivalssportsbook.com/betdetails?id=${bet.uniqueId}`
              navigator.clipboard.writeText(shareUrl)
              alert(`Bet link copied to clipboard`)
            }}
          >
            <FaShareFromSquare size={14} className="text-white md:hidden" />
            <FaShareFromSquare size={16} className="text-white hidden md:block" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default Bet

